import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  GridItem,
  GridContainer,
  FormContainer,
  FormTitle,
  Button,
} from "./CriptoWallets.styles";
import TextField from "../textField/TextField";
import SelectField from "../selectField/SelectField";
import {
  fetchCryptoCurrencies,
  fetchNetworksByCrypto,
  validateWallet,
} from "./CriptoWallet.thunk";
import {
  clearForm,
  setIsFormMode,
  setIsEditMode,
  clearNetworks,
} from "./CriptoWallet.slice";
import { CircularProgress } from "@mui/material";

const CriptoWalletForm = ({ wallet, onCancel, token, onSave }) => {
  const dispatch = useDispatch();
  const { cryptoCurrencies, networks, status, statusNetworks } = useSelector(
    (state) => state.criptoWallet
  );

  const [formValues, setFormValues] = useState({
    ...wallet,
    wallet_address: wallet?.wallet_address || "",
    crypto_currency_code: wallet?.crypto_currency_code || "",
    network_code: wallet?.network_code || "",
    memo_tag: wallet?.memo_tag || "",
    wallet_type: wallet?.wallet_type || "deposit",
    is_active: wallet?.is_active ?? true,
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const walletTypeOptions = [
    { value: "deposit", label: "Deposit" },
    { value: "withdrawal", label: "Withdrawal" },
    { value: "treasury", label: "Treasury" },
  ];

  useEffect(() => {
    if (token) {
      dispatch(fetchCryptoCurrencies({ token }));
    }
  }, [dispatch, token]);

  useEffect(() => {
    if (formValues.crypto_currency_code && token) {
      dispatch(
        fetchNetworksByCrypto({
          token,
          cryptoCode: formValues.crypto_currency_code,
        })
      );
    } else {
      dispatch(clearNetworks());
    }
  }, [formValues.crypto_currency_code, dispatch, token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));

    if (name === "crypto_currency_code") {
      setFormValues((prev) => ({ ...prev, network_code: "" }));
    }

    if (touched[name]) {
      validateField(name, value);
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    validateField(name, value);
  };

  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "wallet_address":
        if (!value) error = "Wallet address is required";
        break;
      case "crypto_currency_code":
        if (!value) error = "Crypto currency is required";
        break;
      case "network_code":
        if (!value) error = "Network is required";
        break;
      case "wallet_type":
        if (!value) error = "Wallet type is required";
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const validateForm = () => {
    const requiredFields = [
      "wallet_address",
      "crypto_currency_code",
      "network_code",
      "wallet_type",
    ];
    const newErrors = {};

    requiredFields.forEach((field) => {
      if (!formValues[field]) {
        newErrors[field] = `${field.replace("_", " ")} is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      // Validar wallet antes de guardar
      const validationResult = await dispatch(
        validateWallet({
          token,
          walletData: {
            wallet_address: formValues.wallet_address,
            crypto_currency_code: formValues.crypto_currency_code,
          },
        })
      ).unwrap();

      if (validationResult.is_valid) {
        // Usar la función onSave en lugar de dispatch directo
        onSave(formValues);

        dispatch(clearForm());
        dispatch(setIsFormMode(false));
        dispatch(setIsEditMode(false));
      } else {
        setErrors({ wallet_address: validationResult.validation_message });
      }
    } catch (error) {
      setErrors({ submit: error.message });
    }
  };

  const handleCancel = () => {
    dispatch(clearForm());
    dispatch(setIsFormMode(false));
    dispatch(setIsEditMode(false));
    if (onCancel) onCancel();
  };

  return (
    <FormContainer>
      <FormTitle>
        {wallet ? "Edit Crypto Wallet" : "Add New Crypto Wallet"}
      </FormTitle>

      <form onSubmit={handleSubmit}>
        <GridContainer>
          <GridItem xs={12} md={6} lg={4} padding="0 15px 15px 0">
            <SelectField
              id="crypto_currency_code"
              name="crypto_currency_code"
              fullWidth
              label="Crypto Currency"
              value={formValues.crypto_currency_code}
              options={cryptoCurrencies}
              required
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.crypto_currency_code}
              touched={touched.crypto_currency_code}
            />
          </GridItem>

          <GridItem xs={12} md={6} lg={4} padding="0 15px 15px 0">
            {statusNetworks === "loading" ? (
              <CircularProgress />
            ) : (
              <SelectField
                id="network_code"
                name="network_code"
                fullWidth
                label="Network"
                value={formValues.network_code}
                options={networks}
                required
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.network_code}
                touched={touched.network_code}
                disabled={!formValues.crypto_currency_code}
              />
            )}
          </GridItem>

          <GridItem xs={12} md={6} lg={4} padding="0 15px 15px 0">
            <SelectField
              id="wallet_type"
              name="wallet_type"
              fullWidth
              label="Wallet Type"
              value={formValues.wallet_type}
              options={walletTypeOptions}
              required
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.wallet_type}
              touched={touched.wallet_type}
            />
          </GridItem>

          <GridItem xs={12} md={6} lg={6} padding="0 15px 15px 0">
            <TextField
              id="wallet_address"
              name="wallet_address"
              fullWidth
              label="Wallet Address"
              value={formValues.wallet_address}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              error={errors.wallet_address}
              touched={touched.wallet_address}
            />
          </GridItem>

          <GridItem xs={12} md={6} lg={4} padding="0 15px 15px 0">
            <TextField
              id="memo_tag"
              name="memo_tag"
              fullWidth
              label="Memo Tag (Optional)"
              value={formValues.memo_tag}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.memo_tag}
              touched={touched.memo_tag}
            />
          </GridItem>
        </GridContainer>

        {errors.submit && (
          <div style={{ color: "red", marginTop: "16px" }}>{errors.submit}</div>
        )}

        <div style={{ marginTop: "20px", display: "flex", gap: "12px" }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={status === "loading"}
          >
            {status === "loading"
              ? "Saving..."
              : wallet
              ? "Update Wallet"
              : "Add Wallet"}
          </Button>
          <Button type="button" variant="outlined" onClick={handleCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </FormContainer>
  );
};

export default CriptoWalletForm;
